import {
  IssueActionProgressEnum,
  IssueStatusEnum,
  IssueTypeEnum,
} from '@issuemanager/constants';
import { z } from 'zod';
import {
  GetManageIssueFormSchemaArgs,
  actionApproverValidation,
  actionDescriptionValidation,
  actionDueDateValidation,
  actionDueDateValidations,
  actionOwnerValidation,
  actionSequentialDueDateValidation,
  actionTitleValidations,
  actionsAtLeastOneValidation,
  baseSchema,
  issueDueDateEarlierThanSLAPastValidation,
} from './common';

export function getManageIssueFormSaveSchema({
  t,
  timezoneOffset,
  SLAs,
  isDateEarlierThanSLA,
  issueStatus,
  issueActionProgress,
  isDraftIssue,
}: GetManageIssueFormSchemaArgs) {
  const noAssignmentValidationNeeded = [
    IssueStatusEnum.IN_REVIEW,
    IssueStatusEnum.OPEN,
    IssueStatusEnum.CLOSED,
  ];
  const noWorkflowValidationNeeded = [
    IssueActionProgressEnum.BLANK,
    IssueActionProgressEnum.NOT_INITIATED,
  ];
  /**
   * fields are optional() because at first all fields can be empty.
   * then according to the issue-status some fields can be required.
   * the validations that depend on issue-status | issue-progress
   * will be done in the superRefine section
   */
  return (
    baseSchema
      .extend({
        ownerId: baseSchema.shape.ownerId.nullable().optional(),
        severity: baseSchema.shape.severity.nullable().optional(),
        dueDate: baseSchema.shape.dueDate.optional(),
        approverId: baseSchema.shape.approverId.nullable().optional(),
        executionType: baseSchema.shape.executionType.optional(),
      })
      /**
       * these validations required using reference to other fields and outside data
       * validation logic is explicitly defined here to make it more readable
       * every error needs to be push individually to the context
       * the first part checks the Assign validations
       * the second part checks the Workflow validations
       */
      .superRefine((data, ctx) => {
        if (!data.title) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['title'],
            message: t('issue:form.errorMessages.title.required'),
          });
        }

        if (!isDraftIssue && !data.description) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['description'],
            message: t('issue:form.errorMessages.description.required'),
          });
        }

        if (!isDraftIssue && !data.severity) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['severity'],
            message: t('issue:form.errorMessages.severity.required'),
          });
        }

        const isAuditFinding = data.issueType === IssueTypeEnum.AUDIT_FINDING;
        const isControlDeficiency =
          data.issueType === IssueTypeEnum.CONTROL_DEFICIENCY;

        if (
          !isDraftIssue &&
          (isAuditFinding || isControlDeficiency) &&
          !data.classification
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['classification'],
            message: t('issue:form.errorMessages.classification.required'),
          });
        }

        if (
          !isDraftIssue &&
          (isAuditFinding || isControlDeficiency) &&
          !data.rootCause
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['rootCause'],
            message: t('issue:form.errorMessages.rootCause.required'),
          });
        }

        if (!isDraftIssue && isAuditFinding && !data.recommendations) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['recommendations'],
            message: t('issue:form.errorMessages.recommendations.required'),
          });
        }

        // Issue due date SLA validation
        if (!isDraftIssue && data.dueDate && data.severity) {
          issueDueDateEarlierThanSLAPastValidation({
            ctx,
            SLAs,
            isDateEarlierThanSLA,
            t,
            data: {
              dueDate: data.dueDate,
              severity: data.severity,
            },
          });
        }

        // Assignment validations
        if (!isDraftIssue) {
          if (
            !(
              issueStatus &&
              noAssignmentValidationNeeded.includes(
                issueStatus as IssueStatusEnum,
              )
            )
          ) {
            if (!data.ownerId) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['ownerId'],
                message: t('issue:form.errorMessages.owner.required'),
              });
            }
            if (!data.approverId) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['approverId'],
                message: t('issue:form.errorMessages.approver.required'),
              });
            }
            if (!data.dueDate) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dueDate'],
                message: t('issue:form.errorMessages.dueDate.required'),
              });
            }
          }

          // Workflow validations
          if (
            !(
              issueActionProgress &&
              noWorkflowValidationNeeded.includes(
                issueActionProgress as IssueActionProgressEnum,
              )
            )
          ) {
            actionsAtLeastOneValidation({
              actions: data.actions,
              ctx,
              t,
              isDraftIssue,
            });

            data.actions?.forEach((action, index) => {
              actionDescriptionValidation({
                data: {
                  description: action.description,
                  index,
                },
                ctx,
                t,
              });
              actionDueDateValidation({
                data: {
                  dueDate: action.dueDate,
                  index,
                },
                ctx,
                t,
              });
              actionOwnerValidation({
                data: {
                  ownerId: action.ownerId,
                  index,
                },
                ctx,
                t,
              });

              actionApproverValidation({
                data: {
                  approverId: action.approverId,
                  status: action.status,
                  index,
                },
                ctx,
                t,
              });
            });
          }
        }

        // [title | unique title | due date past | due date before issue |
        // due date sequential] validations always happen
        const standardizedTitles = new Set<string>();
        data.actions?.forEach((action, index) => {
          actionTitleValidations({
            data: {
              action,
              actions: data.actions || [],
              index,
              standardizedTitles,
            },
            isDateEarlierThanSLA,
            SLAs,
            ctx,
            t,
          });
          actionDueDateValidations({
            data: {
              action,
              dueDate: data.dueDate,
              index,
            },
            timezoneOffset,
            SLAs,
            ctx,
            t,
            skipPastDateValidation: true,
          });
          actionSequentialDueDateValidation({
            data: {
              action,
              actions: data.actions || [],
              dueDate: action.dueDate,
              index,
              executionType: data.executionType,
            },
            ctx,
            t,
          });
        });
      })
  );
}

export type ManageIssueFormSaveSchemaType = ReturnType<
  typeof getManageIssueFormSaveSchema
>;
